import {
  OidcMetadata,
  UserManager,
  UserManagerSettings,
  WebStorageStateStore,
} from 'oidc-client-ts';

import { services } from '@src/services';
import {
  clearState,
  loadEnterpriseConnection,
  saveEnterpriseConnection,
} from './services/local-storage-service';
import { authActions } from './modules/auth';
import { browserHistory, store } from './store';
import { apiUtils } from './utils';

const env = services.envService.getEnvironmentalVariables();

const EnterpriseConnections: { [k: string]: string } = {
  '/apoteket': env.MAD_APOTEKET_ADFS,
  '/ingridrnd': 'ingrid-rnd',
};

const OIDC_BASE_SCOPE = 'openid email';
const ORG_ID_SCOPE_PREFIX = 'urn:zitadel:iam:org:id:';
const FORCED_IDP_SCOPE_PREFIX = 'urn:zitadel:iam:org:idp:id:';

const isZitadelIdpId = (connection?: string) => /^\d+$/.test(connection || '');

const getScopeForEnterpriseConnection = (connection?: string) => {
  if (!isZitadelIdpId(connection) || !env.MAD_ZITADEL_ORG_ID) {
    return OIDC_BASE_SCOPE;
  }

  return `${OIDC_BASE_SCOPE} ${ORG_ID_SCOPE_PREFIX}${env.MAD_ZITADEL_ORG_ID} ${FORCED_IDP_SCOPE_PREFIX}${connection}`;
};

export const getEnterpriseConnectionWithPathname = (pathname: string) =>
  EnterpriseConnections[pathname];

const getCurrentEnterpriseConnection = () => {
  const fromPath = getEnterpriseConnectionWithPathname(window.location.pathname);
  if (fromPath) {
    saveEnterpriseConnection(fromPath);
    return fromPath;
  }
  return loadEnterpriseConnection();
};

const clearOidcState = () => {
  const keys = Object.keys(window.localStorage);
  keys.filter(key => key.startsWith('oidc.')).forEach(key => window.localStorage.removeItem(key));
};

export const logoutFromAuthProvider = () => {
  const authority = normalizeAuthority(env.MAD_AUTH0_DOMAIN);
  const redirectUrl = encodeURIComponent(apiUtils.getRedirectionUrl());
  const clientId = encodeURIComponent(env.MAD_AUTH0_CLIENT_ID);

  clearOidcState();
  clearState();

  window.location.assign(
    `${authority}/oidc/v1/end_session?client_id=${clientId}&post_logout_redirect_uri=${redirectUrl}`
  );
};

const normalizeAuthority = (authDomain: string) => {
  const isHttpUrl = /^https?:\/\//.test(authDomain);
  const authority = isHttpUrl ? authDomain : `https://${authDomain}`;
  return authority.replace(/\/$/, '');
};

const createMetadata = (authority: string): Partial<OidcMetadata> => ({
  issuer: authority,
  authorization_endpoint: `${authority}/oauth/v2/authorize`,
  token_endpoint: `${authority}/oauth/v2/token`,
  userinfo_endpoint: `${authority}/oidc/v1/userinfo`,
  jwks_uri: `${authority}/oauth/v2/keys`,
  end_session_endpoint: `${authority}/oidc/v1/end_session`,
  introspection_endpoint: `${authority}/oauth/v2/introspect`,
  revocation_endpoint: `${authority}/oauth/v2/revoke`,
});

const parseHashParams = (hash: string) => {
  if (!hash || hash === '#') {
    return new URLSearchParams();
  }

  const hashValue = hash.replace(/^#/, '');
  const isParamsHash = hashValue.includes('=');

  if (!isParamsHash) {
    return new URLSearchParams();
  }

  return new URLSearchParams(hashValue);
};

const hasAuthParams = ({ search, hash }: { search: string; hash: string }) => {
  const searchParams = new URLSearchParams(search);
  const hashParams = parseHashParams(hash);

  const hasInSearch =
    (searchParams.has('code') && searchParams.has('state')) ||
    searchParams.has('error') ||
    searchParams.has('error_description');

  const hasInHash =
    (hashParams.has('code') && hashParams.has('state')) ||
    hashParams.has('error') ||
    hashParams.has('error_description');

  return hasInSearch || hasInHash;
};

const deleteAuthParams = (params: URLSearchParams) => {
  params.delete('code');
  params.delete('state');
  params.delete('session_state');
  params.delete('id_token');
  params.delete('access_token');
  params.delete('token_type');
  params.delete('expires_in');
  params.delete('scope');
  params.delete('error');
  params.delete('error_description');
};

const clearAuthParams = () => {
  const { pathname, search, hash } = window.location;
  const searchParams = new URLSearchParams(search);
  const hashParams = parseHashParams(hash);

  deleteAuthParams(searchParams);
  deleteAuthParams(hashParams);

  const nextSearch = searchParams.toString();
  const nextHashParams = hashParams.toString();
  const nextHash = nextHashParams ? `#${nextHashParams}` : hash.includes('=') ? '' : hash;

  const nextUrl = `${pathname}${nextSearch ? `?${nextSearch}` : ''}${nextHash}`;

  window.history.replaceState(window.history.state, document.title, nextUrl);
};

const extractErrorMessage = (error: unknown) => {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'Unknown error';
};

const buildAuthErrorMessage = (error: unknown) => {
  const errorMessage = extractErrorMessage(error);
  return `Authentication Error: ${errorMessage}`;
};

export const configureAuth = () => {
  const authority = normalizeAuthority(env.MAD_AUTH0_DOMAIN);
  const settings: UserManagerSettings = {
    authority,
    client_id: env.MAD_AUTH0_CLIENT_ID,
    redirect_uri: apiUtils.getRedirectionUrl(),
    response_type: 'code',
    scope: OIDC_BASE_SCOPE,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    metadata: createMetadata(authority),
  };
  const manager = new UserManager(settings);

  const dispatchAuthError = (error: unknown) => {
    console.error('MAD auth failure', error);
    store.dispatch(authActions.authenticateError(buildAuthErrorMessage(error)));
  };

  const show = async () => {
    store.dispatch(authActions.authenticate());

    try {
      await manager.clearStaleState();

      if (hasAuthParams(window.location)) {
        try {
          const user = await manager.signinRedirectCallback();
          const token = user && user.access_token;

          if (!token) {
            throw new Error('Missing access_token in authorization response');
          }

          clearAuthParams();
          const returnTo = (user.state as { returnTo?: unknown } | undefined)?.returnTo;
          if (typeof returnTo === 'string') {
            let destination: URL | undefined;
            try {
              destination = new URL(returnTo, window.location.origin);
            } catch {
              destination = undefined;
            }
            if (destination?.origin === window.location.origin) {
              browserHistory.replace(
                `${destination.pathname}${destination.search}${destination.hash}`
              );
            }
          }
          store.dispatch(authActions.authenticateSuccess(token));
          return;
        } catch (callbackError) {
          clearAuthParams();
          throw callbackError;
        }
      }

      const connection = getCurrentEnterpriseConnection();
      await manager.signinRedirect({
        state: {
          returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}`,
        },
        scope: getScopeForEnterpriseConnection(connection),
        extraQueryParams: {
          audience: env.MAD_AUTH0_AUDIENCE,
          ...(connection ? { connection } : {}),
        },
      });
    } catch (error) {
      dispatchAuthError(error);
    }
  };

  return {
    show,
    hide: () => undefined,
  };
};
